import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { isEqual } from "lodash";
import Candidate from "../apiResources/Candidate";
import cache from "../cache";
import { usePagination, useFlash, useModal } from "../hooks";
import BasePage from "./BasePage";
import Pagination from "./library/Pagination";
import Loader from "./library/Loader";
import Button, { ButtonIcon } from "./library/Button";
import FlashMessage from "./shared/general/FlashMessage";
import CandidateListCard from "./AgencyCandidatesIndex/CandidateListCard";
import CandidateListFilterBar from "./AgencyCandidatesIndex/CandidateListFilterBar";
import CandidateCreateModal from "./CandidateCreateModal";
import CandidateEditModal from "./CandidateEditModal";
import TextLink from "./library/TextLink";
import Container from "./library/Container";

const applicationStatus = ["awaiting_feedback", "interviewing", "rejected"];

type ApplicationStatus = (typeof applicationStatus)[number];

export type FilterData = {
  status: ApplicationStatus[];
};

const defaultFilters: FilterData = {
  status: [],
};

const CandidatesListPage = () => {
  const initial = getInitialData();

  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [query, setQuery] = useState(initial.query);
  const [filters, setFilters] = useState<FilterData>(initial.filters);
  const pagination = usePagination({ page: initial.page });
  const [totalCandidates, setTotalCandidates] = useState<number>(
    initial.totalCandidates
  );

  const fetchCandidates = async () => {
    setLoading(true);

    const response = await Candidate.where({
      application_status: getStatus(filters.status),
      full_name: { match: query },
    })
      .includes({ recruiter: ["currency"] })
      .order({ updated_at: "desc" })
      .page(pagination.page)
      .per(pagination.per)
      .stats({ total: "count" })
      .all();

    setCandidates(response.data);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    setTotalCandidates(response.meta.stats.total.count);

    setLoading(false);
  };

  useEffect(() => {
    void fetchCandidates();
  }, [filters, pagination.page, query]);

  useEffect(() => {
    cache.set("candidate-list", {
      query,
      filters,
      page: pagination.page,
      total: totalCandidates,
    });
  }, [query, filters, pagination.page, candidates, totalCandidates]);

  useEffect(() => {
    pagination.setPage(1);
  }, [filters, query]);

  const resetFilters = () => {
    if (!isEqual(filters, defaultFilters)) {
      setFilters(defaultFilters);
    }
    setQuery("");
    pagination.setPage(1);
  };

  const flash = useFlash();
  const createModal = useModal(false);

  const onCreateSuccess = (candidate: Candidate) => {
    // Push the new candidate to the start of the candidates list and remove the
    // existing last item so we still showing the same number of results as before.
    const candidatesWithNew = [candidate, ...candidates.slice(0, -1)];
    setCandidates(candidatesWithNew);
    setTotalCandidates(totalCandidates + 1);

    // We optimistically push the candidate onto the local data, however if the
    // user has searched, filtered or is not on the first page it won't make
    // sense to show this new candidate under those conditions. Setting this
    // state will only trigger a refresh when the data is not the defaults.
    resetFilters();

    flash.show({ message: "Successfully created new candidate" });
    createModal.close();
  };

  const editModal = useModal(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate>(null);

  const onEditStart = async (candidate: Candidate) => {
    const candidateWithCv = (
      await Candidate.selectExtra(["cv"])
        .includes({ recruiter: ["currency"] })
        .find(candidate.id)
    ).data;
    setEditingCandidate(candidateWithCv);
    editModal.open();
  };

  const onEditSuccess = (updatedCandidate: Candidate) => {
    const candidatesWithUpdated = candidates.map((candidateInList) =>
      candidateInList.id === updatedCandidate.id
        ? updatedCandidate
        : candidateInList
    );
    setCandidates(candidatesWithUpdated);
    flash.show({ message: "Successfully updated candidate" });
    editModal.close();
  };

  return (
    <BasePage title="Candidates">
      <Container>
        <FlashMessage
          message={flash.message}
          success
          showFlashMessage={flash.visible}
        />

        <CandidateCreateModal
          isOpen={createModal.isOpen}
          onDismiss={createModal.close}
          onSuccess={onCreateSuccess}
        />

        <CandidateEditModal
          candidate={editingCandidate}
          isOpen={editModal.isOpen}
          onDismiss={editModal.close}
          onSuccess={onEditSuccess}
        />

        <ButtonHeader>
          <Button
            type="button"
            secondary
            onClick={createModal.open}
            id="add-candidate-button"
          >
            <ButtonIcon icon={faPlus} />
            New Candidate
          </Button>
        </ButtonHeader>

        <CandidateListFilterBar
          initialQuery={query}
          onQueryChange={setQuery}
          filters={filters}
          onFiltersChange={setFilters}
        />

        {loading && <Loader text="Loading candidates..." />}

        {!loading && !candidates.length && (
          <div>
            Sorry, you have no candidates that match that search. Try searching
            again or{" "}
            <TextLink onClick={createModal.open}>add a new candidate</TextLink>.
          </div>
        )}

        {!loading && Boolean(candidates.length) && (
          <div data-test-id="candidates-list">
            {candidates.map((candidate) => (
              <CandidateListCard
                key={candidate.id}
                candidate={candidate}
                onEditClick={onEditStart}
              />
            ))}
            <Pagination pagination={pagination} total={totalCandidates} />
          </div>
        )}
      </Container>
    </BasePage>
  );
};

function getInitialData() {
  const cached = cache.get("candidate-list");

  return {
    cached: Boolean(cached),
    filters: cached?.filters ?? defaultFilters,
    query: cached?.query ?? "",
    page: cached?.page ?? null,
    totalCandidates: cached?.total ?? 0,
  };
}

function getStatus(status: ApplicationStatus[]) {
  const result: string[] = [];

  if (status.includes("awaiting_feedback")) {
    result.push("awaiting_feedback");
  }

  if (status.includes("interviewing")) {
    result.push(
      "interview_requested",
      "interviewing",
      "interview_requested_telephone",
      "interview_requested_first",
      "interview_requested_second",
      "competencies_required",
      "confirmed_interview",
      "offered_job"
    );
  }

  if (status.includes("rejected")) {
    result.push(
      "rejected_candidate",
      "duplicate",
      "unsuccessful",
      "rejected_job"
    );
  }

  return result;
}

const ButtonHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

export default CandidatesListPage;
